import type { MessageRouter } from "@pdfking/functionpool"
import type { Page, PageNumber } from "@pdfking/renderer-api"
import type {
  AnyGUID,
  AsyncSequence,
  Atom,
  Partial,
  StringGUID,
} from "@pdfking/tfunc"
import {
  ArrayToIterable,
  BlobToUint8,
  First,
  GuidToString,
  Identity,
  IsArray,
  Map as MapSync,
  MapAsync,
  MapAsyncConcurrent,
  MethodDispatcher,
  Range,
  SafePromise,
  StreamCreate,
  UUID,
  Values,
  Void,
} from "@pdfking/tfunc"
import { AtomHash } from "@pdfking/tfunc"

import { CreateRemoteFile } from "../files/CreateRemoteFile"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { FileContentGUID } from "../methods/FileContentGUID"
import type { APIRequest } from "../types/api/APIRequest"
import type { APIResponse } from "../types/api/APIResponse"
import type { PdfEngine } from "../types/engine/PdfEngine"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { GeneratedFile } from "../types/files/cached/GeneratedFile"
import type { RemoteFile } from "../types/files/cached/RemoteFile"
import type { SinglePageImage } from "../types/files/cached/SinglePageImage"
import type { SinglePagePdf } from "../types/files/cached/SinglePagePdf"
import type { VirtualFile } from "../types/files/VirtualFile"
import { VirtualFileType } from "../types/files/VirtualFileType"
import type { TaskResult } from "../types/tasks/input/TaskResult"
import type { FilePlottingContext } from "../types/tasks/options/TaskPlotOptions"
import { TaskName } from "../types/tasks/TaskName"
import type { TaskOptions } from "../types/tasks/TaskOptions"
import { CreatePdfEngineDefaults } from "./CreatePdfEngineDefaults"

const { ceil, min, max } = Math

const make_task_id = (t: TaskName, ...args: Atom[]): Promise<StringGUID> =>
  AtomHash([t, ...args]).then(GuidToString)

// Top-level message queue router with post-processing
const ForwardTask = (
  router: MessageRouter<APIRequest, APIResponse>,
  options: TaskOptions,
  ...input: AsyncSequence<APIRequest>[]
): AsyncGenerator<VirtualFile> => {
  const responses = router(ArrayToIterable([options], ...input))
  return MapAsync(response => (response as TaskResult).content, responses)
}

export const CreateEngineVirtual = (
  config: Partial<PdfEngineOptions>,
  router: MessageRouter<APIRequest, APIResponse>
): PdfEngine => {
  const options = CreatePdfEngineDefaults(config)

  const mapSinglePage = async function* <T>(
    processor: (page: SinglePagePdf) => AsyncSequence<T>,
    files: AsyncSequence<VirtualFile>
  ): AsyncGenerator<T> {
    for await (const file of files) {
      for await (const page of SplitPdf(file)) {
        yield* processor(page)
      }
    }
  }

  const innerSplit = async function* (
    source: GeneratedFile | RemoteFile
  ): AsyncGenerator<SinglePagePdf> {
    const max_pages = options.max_split_pages

    // Closure to run split operation for a given page range
    const runSplit = async function* (
      start_page: PageNumber,
      end_page: PageNumber
    ): AsyncGenerator<SinglePagePdf> {
      yield* ForwardTask(router, {
        options,
        enum: TaskName.TASK_SPLIT,
        task_id: await make_task_id(
          TaskName.TASK_SPLIT,
          FileContentGUID(source),
          start_page,
          end_page
        ),
        source,
        start_page,
        end_page,
      }) as AsyncGenerator<SinglePagePdf>
    }

    // First batch: pages 1 to max_split_pages
    const first_batch = runSplit(1 as PageNumber, max_pages)
    const { value: first_page } = await SafePromise(First(first_batch))

    if (first_page) {
      // Yield the first page
      yield first_page

      // Compute remaining batches
      const batch_count = ceil((first_page.page_count - max_pages) / max_pages)

      if (batch_count > 0) {
        // Generate array of generators for remaining batches
        const remaining_batches = MapSync(batch_index => {
          const start_page = (max_pages +
            batch_index * max_pages +
            1) as PageNumber
          const end_page = min(
            start_page + max_pages - 1,
            first_page.page_count
          ) as PageNumber

          return runSplit(start_page, end_page)
        }, Range(batch_count))

        yield* MapAsyncConcurrent(Identity, first_batch, ...remaining_batches)
      } else {
        yield* first_batch
      }
    }
  }

  const SplitPdf = MethodDispatcher<
    [VirtualFile],
    AsyncGenerator<SinglePagePdf>
  >({
    [VirtualFileType.SINGLE_PAGE_IMAGE]: p => ArrayToIterable([p.source]),
    [VirtualFileType.SINGLE_PAGE_PDF]: p => ArrayToIterable([p]),
    [VirtualFileType.MULTI_PAGE_PDF]: p => ArrayToIterable(p.pages),
    [VirtualFileType.ZIP_FILE]: p => mapSinglePage(p => [p], Values(p.files)),
    [VirtualFileType.GENERATED_FILE]: innerSplit,
    [VirtualFileType.REMOTE_FILE]: innerSplit,
  })

  const rasterizeSingle = async function* (
    source: SinglePagePdf
  ): AsyncGenerator<SinglePageImage> {
    yield* ForwardTask(router, {
      options,
      enum: TaskName.TASK_RASTERIZE,
      task_id: await make_task_id(
        TaskName.TASK_RASTERIZE,
        FileContentGUID(source)
      ),
      source,
    }) as AsyncGenerator<SinglePageImage>
  }

  const RasterizePdf = MethodDispatcher<
    [VirtualFile],
    AsyncGenerator<SinglePageImage>
  >(
    {
      [VirtualFileType.SINGLE_PAGE_IMAGE]: p => ArrayToIterable([p]),
      [VirtualFileType.SINGLE_PAGE_PDF]: rasterizeSingle,
      [VirtualFileType.MULTI_PAGE_PDF]: p =>
        mapSinglePage(rasterizeSingle, p.pages),
    },
    p => mapSinglePage(rasterizeSingle, [p])
  )

  const MergePdf = async (
    files: AsyncSequence<[PageNumber, PageNumber, SinglePagePdf] | VirtualFile>,
    task_id: AnyGUID | undefined = undefined
  ): Promise<GeneratedFile> => {
    let page_index = 0 as PageNumber

    const pages = async function* (): AsyncGenerator<
      [PageNumber, PageNumber, SinglePagePdf]
    > {
      for await (const page of files) {
        if (IsArray(page)) {
          yield page as [PageNumber, PageNumber, SinglePagePdf]
          page_index = max(page_index, page[1]) as PageNumber
        } else {
          const page_count = page_index
          for await (const pdf of SplitPdf(page)) {
            page_index = (page_index + 1) as PageNumber
            yield [page_index, (page_count + pdf.page_count) as PageNumber, pdf]
          }
        }
      }
    }

    return First(
      ForwardTask(
        router,
        {
          options,
          enum: TaskName.TASK_MERGE,
          task_id: await make_task_id(TaskName.TASK_MERGE, task_id ?? UUID()),
        },
        MapAsync(
          ([page_number, page_count, file]) => ({
            enum: TaskName.CONTENT_PAGED,
            page_number,
            page_count,
            content: file,
          }),
          pages()
        )
      )
    ) as Promise<GeneratedFile>
  }

  const ZipFiles = async (
    files: AsyncSequence<[string, VirtualFile]>,
    task_id: AnyGUID | undefined = undefined
  ): Promise<GeneratedFile> =>
    First(
      ForwardTask(
        router,
        {
          options,
          enum: TaskName.TASK_ZIP,
          task_id: await make_task_id(TaskName.TASK_ZIP, task_id ?? UUID()),
        },
        MapAsync(
          ([name, file]) => ({
            enum: TaskName.CONTENT_FILE,
            content: file,
            name,
          }),
          files
        )
      )
    ) as Promise<GeneratedFile>

  const PlotPdf = async (
    page: Page,
    context: FilePlottingContext = {},
    task_id: AnyGUID | undefined = undefined
  ): Promise<SinglePagePdf> =>
    First(
      ForwardTask(router, {
        options,
        enum: TaskName.TASK_PLOT,
        page,
        context,
        task_id: GuidToString(task_id || UUID()),
      })
    ) as Promise<SinglePagePdf>

  const engine: PdfEngine = {
    config: options,
    SplitPdf,
    RasterizePdf,
    MergePdf,
    ZipFiles,
    PlotPdf,
    CreateRemoteFile,
    GenerateFile: (stream, content_type) =>
      GenerateFile(engine, stream, content_type, Void),
    RequestContent: (file, emit) =>
      RequestBlob(engine, file, emit).then(
        ({ stream, size }) =>
          new Response(StreamCreate(MapAsync(BlobToUint8, stream)), {
            headers: {
              "content-length": size.toString(),
            },
          })
      ),
  }

  return engine
}
